import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { useEffect, useState } from "react";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import ActionMenu from "@/components/Usados/ActionMenu";
import { Modal } from "@/components/Usados/modal";
import Button from "@/components/ui/button/Button";
import { useNavigate } from "react-router";
import { Eye, Pencil, Plus, Trash } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Rol } from "@/types/roles";
import { apiFetch } from "@/service/api";

interface Proyecto {
  id: number;
  nombre: string;
  descripcion: string;
  fecha_inicio: string;
  fecha_entrega_estimada: string;
  presupuesto: number;
  tipo_proyecto_id: number;
  estado_proyecto_id: number;
}

export default function ListarProyectos() {
  const { user } = useAuth();
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [proyectoToDelete, setProyectoToDelete] = useState<number | null>(null);
  const navigate = useNavigate();
  const rolId: number = user.rol;
  const canDelete = rolId!== Rol.COLABORADOR && rolId!== Rol.ASISTENTE;

  const fetchProyectos = async () => {
    try {
      const response = await apiFetch(`proyectos/find-all`);
      if (response.ok) setProyectos(await response.json());
    } catch (error) {
      console.error("Error fetching proyectos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProyectos();
  }, []);

  const handleDelete = (id: number) => {
    setProyectoToDelete(id);
    setIsModalOpen(true);
  };
  const confirmDelete = async () => {
    if (!proyectoToDelete) return;
    try {
      const response = await apiFetch(
        `proyectos/delete/${proyectoToDelete}`,
        { method: "DELETE" },
      );
      if (response.ok) {
        setProyectos(proyectos.filter((p) => p.id!== proyectoToDelete));
        setIsModalOpen(false);
        setProyectoToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting proyecto:", error);
    }
  };

  const columns: Column<Proyecto>[] = [
    { label: "Nombre", key: "nombre" },
    { label: "Fecha Inicio", key: "fecha_inicio" },
    { label: "Fecha Entrega", key: "fecha_entrega_estimada" },
    {
      label: "Presupuesto",
      key: "presupuesto",
      render: (value: any) =>
        new Intl.NumberFormat("es-CO", {
          style: "currency",
          currency: "COP",
        }).format(value),
    },
    {
      label: "Acciones",
      key: "actions",
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              label: "Ver",
              icon: <Eye className="size-4" />,
              onClick: () =>
                navigate(`/gestion_proyectos/proyectos/ver/${row.id}`),
            },
            {
              label: "Editar",
              icon: <Pencil className="size-4" />,
              onClick: () =>
                navigate(`/gestion_proyectos/proyectos/update/${row.id}`),
            },
            ...(canDelete
             ? [
                  {
                    label: "Eliminar",
                    icon: <Trash className="size-4" />,
                    onClick: () => handleDelete(row.id),
                    className: "text-red-500 hover:bg-red-50",
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageBreadcrumb pageTitle="Gestion de Proyectos" />
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <div className="flex flex-col items-center justify-between gap-4 mb-6 sm:flex-row">
          <h3 className="font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl">
            Listado de Proyectos
          </h3>
          <Button
            onClick={() => navigate("/gestion_proyectos/proyectos/create")}
            startIcon={<Plus className="size-4" />}
          >
            Nuevo Proyecto
          </Button>
        </div>
        {loading? (
          <div className="flex items-center justify-center p-10">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-brand-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <ResponsiveTable columns={columns} data={proyectos} />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w- p-6"
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center justify-center w-12 h-12 mb-4 bg-red-100 rounded-full dark:bg-red-500/20">
            <Trash className="text-red-600 size-6 dark:text-red-500" />
          </div>
          <h4 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
            Confirmar Eliminacion
          </h4>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            ¿Estas seguro de que deseas eliminar este proyecto? Esta accion no
            se puede deshacer.
          </p>
          <div className="flex justify-center w-full gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="w-full"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              className="w-full bg-red-600 hover:bg-red-700 text-white border-none"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}